/**
 * Text key component. Represents a single text key in the virtual keyboard.
 */
import Focus from '../../atoms/container/keyTextFocus';
import KeyText from '../../atoms/text/keyText';
import RelativeContainer from '../../atoms/container/relativeContainer';
import VirtualKeyboardContext from '../../../../shared/components/utils/context/virtualKeyboardContext';
import { magicRemoteSelector } from '../../../../shared/store/slices/magicRemote.slice';
import { useAppSelector } from '../../../../shared/store/hooks';
import { useContext, useMemo, useState } from 'react';

/**
 * Text key component props
 */
interface Props {
  char: string;
  index: number;
}

/**
 * Text key component. Represents a single text key in the virtual keyboard.
 */
export default function TextKey({ char, index }: Props) {
  const { props, focused } = useTextKey(index);
  // Render
  return (
    <KeyText {...props} focused={focused}>
      {char}
      {focused && (
        <RelativeContainer>
          <Focus />
        </RelativeContainer>
      )}
    </KeyText>
  );
}

/**
 * Custom hook to handle the text key component.
 * @param index - The index of the key.
 * @returns The props, hovered, focused and onClick functions.
 */
function useTextKey(index: number) {
  // Selectors
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { listIndex, listFocused, onClick } = useContext(
    VirtualKeyboardContext
  );

  // States
  const [hovered, setHovered] = useState(false);

  // Memoized values
  const { focused, props } = useMemo(() => {
    const focused = !enabled && listFocused && index === listIndex;
    return {
      focused: focused || hovered,
      props: {
        onClick: () => {
          onClick(index);
        },
        onPointerLeave: () => setHovered(focused),
        onPointerEnter: () => setHovered(true),
      },
    };
  }, [hovered, enabled, listFocused, listIndex, onClick]);

  // Return values
  return { focused, props };
}
